import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

export interface WorkflowStep {
  id: string;
  label: string;
  shortLabel: string;
  isComplete: boolean;
  isAccessible: boolean;
}

interface WorkflowStepperProps {
  steps: WorkflowStep[];
  activeStepId: string;
  onStepClick: (stepId: string) => void;
}

export const WorkflowStepper: React.FC<WorkflowStepperProps> = ({
  steps,
  activeStepId,
  onStepClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef<HTMLButtonElement>(null);

  // When the active step changes, keep its header in view. On narrow viewports the
  // earliest steps scroll off-screen once a later step (e.g. Push to EMR) is active,
  // so navigating back to an earlier tab -- including via the "fix this on the
  // Eligibility tab" required-field link -- should bring that tab's header into view.
  // Scrolls only the stepper's own horizontal overflow, never the page.
  useEffect(() => {
    const container = scrollRef.current;
    const active = activeStepRef.current;
    if (!container || !active) return;
    const containerRect = container.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const margin = 16;
    if (activeRect.left < containerRect.left) {
      container.scrollBy({
        left: activeRect.left - containerRect.left - margin,
        behavior: 'smooth',
      });
    } else if (activeRect.right > containerRect.right) {
      container.scrollBy({
        left: activeRect.right - containerRect.right + margin,
        behavior: 'smooth',
      });
    }
  }, [activeStepId]);

  return (
    <div className='flex items-center border-b border-gray-200'>
      <div
        ref={scrollRef}
        className='flex items-center overflow-x-auto scrollbar-hide'
      >
        {steps.map((step, index) => {
          const isActive = step.id === activeStepId;
          const isComplete = step.isComplete;

          return (
            <button
              key={step.id}
              ref={isActive ? activeStepRef : undefined}
              onClick={() => step.isAccessible && onStepClick(step.id)}
              disabled={!step.isAccessible}
              className={`
                relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium
                transition-all duration-150 whitespace-nowrap border-b-2 -mb-px
                ${
                  isActive
                    ? 'border-gray-900 text-gray-900'
                    : isComplete
                      ? 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                      : step.isAccessible
                        ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                        : 'border-transparent text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {/* Step number or check */}
              <span
                className={`
                flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold
                ${
                  isComplete
                    ? 'bg-emerald-500 text-white'
                    : isActive
                      ? 'bg-gray-900 text-white'
                      : step.isAccessible
                        ? 'bg-gray-200 text-gray-600'
                        : 'bg-gray-100 text-gray-500'
                }
              `}
              >
                {isComplete ? (
                  <Check className='w-3 h-3' strokeWidth={3} />
                ) : (
                  index + 1
                )}
              </span>
              {step.shortLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowStepper;
